import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { VehicleValuationHistorySkeleton } from "./_components/vehicle-valuation-history-skeleton";
import { VehicleValuationHistoryTable } from "./_components/vehicle-valuation-history-table";
import { useVehicleValuationHistory } from "./_hooks/use-vehicle-valuation-history";

interface VehicleValuationHistoryProps {
  vehicleUuid: string;
}

export function VehicleValuationHistory({ vehicleUuid }: VehicleValuationHistoryProps) {
  const { t } = useTranslation(["fab", "common"]);
  const {
    isPending,
    isError,
    isEmpty,
    displayEntries,
    hasMore,
    isExpanding,
    showAll,
    toggleShowAll,
    skeletonRowCount,
  } = useVehicleValuationHistory(vehicleUuid);

  const renderCardContent = () => {
    if (isPending) {
      return <VehicleValuationHistorySkeleton rowCount={skeletonRowCount} />;
    }

    if (isError) {
      return (
        <div className="px-6">
          <Alert variant="error">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{t("fab:VehicleValuationHistoryLoadError")}</AlertDescription>
          </Alert>
        </div>
      );
    }

    if (isEmpty) {
      return <p className="text-muted-foreground px-6 text-sm">{t("fab:NoPreviousConfirmedVehicleValuations")}</p>;
    }

    return (
      <>
        <VehicleValuationHistoryTable entries={displayEntries} />

        {isExpanding ? (
          <div className="flex justify-center py-1">
            <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} />
          </div>
        ) : (
          (hasMore || showAll) && (
            <button
              className="text-muted-foreground hover:text-foreground cursor-pointer px-6 pt-1 text-xs"
              onClick={toggleShowAll}
            >
              {showAll ? t("common:Collapse") : t("common:LoadMore")}
            </button>
          )
        )}
      </>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t("fab:VehicleValuationHistory")}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">{renderCardContent()}</CardContent>
    </Card>
  );
}
